import { useEffect, useState } from "react";

/**
 * Single source of truth for viewport-class. Components branch on this rather
 * than each duplicating media-query logic. Returns the *class*, not the raw
 * width — keeps the API discrete and stable across resizes within a class.
 *
 * Breakpoints chosen for Sibit's UI:
 *   mobile  : <  768px  → single-column / tab-switched layouts
 *   tablet  : 768-1199  → 2-3 columns, slightly compact
 *   desktop : >= 1200   → full kanban (6 columns)
 */
export type Viewport = "mobile" | "tablet" | "desktop";

const MOBILE_MAX = 767;
const TABLET_MAX = 1199;

function classify(width: number): Viewport {
  if (width <= MOBILE_MAX) return "mobile";
  if (width <= TABLET_MAX) return "tablet";
  return "desktop";
}

export function useViewport(): Viewport {
  const [vp, setVp] = useState<Viewport>(() =>
    typeof window === "undefined" ? "desktop" : classify(window.innerWidth),
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    let raf = 0;
    const onResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setVp(classify(window.innerWidth)));
    };
    window.addEventListener("resize", onResize, { passive: true });
    return () => {
      window.removeEventListener("resize", onResize);
      cancelAnimationFrame(raf);
    };
  }, []);
  return vp;
}
